import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('hh_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors with token refresh
let isRefreshing = false;
let refreshSubscribers = [];

function onRefreshed(newToken) {
  refreshSubscribers.forEach((cb) => cb(newToken));
  refreshSubscribers = [];
}

function addRefreshSubscriber(cb) {
  refreshSubscribers.push(cb);
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      // If this was already a refresh attempt, log out
      if (originalRequest.url?.includes('/auth/refresh') || originalRequest.url?.includes('/auth/login')) {
        localStorage.removeItem('hh_token');
        localStorage.removeItem('hh_user');
        if (window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin/login') {
          window.location.href = '/admin/login';
        }
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve) => {
          addRefreshSubscriber((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(api(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const token = localStorage.getItem('hh_token');
        if (!token) throw new Error('No token');
        const res = await axios.post(`${API}/auth/refresh`, null, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const newToken = res.data.token;
        localStorage.setItem('hh_token', newToken);
        localStorage.setItem('hh_user', JSON.stringify(res.data.user));
        isRefreshing = false;
        onRefreshed(newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        refreshSubscribers = [];
        localStorage.removeItem('hh_token');
        localStorage.removeItem('hh_user');
        if (window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin/login') {
          window.location.href = '/admin/login';
        }
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth
export const login = (username, password) => api.post('/auth/login', { username, password });
export const changePassword = (current_password, new_password) => api.post('/auth/change-password', { current_password, new_password });
export const getMe = () => api.get('/auth/me');
export const refreshToken = () => api.post('/auth/refresh');

// Public endpoints
export const getPublicLogements = (params) => api.get('/public/logements', { params });
export const getPublicLogement = (id) => api.get(`/public/logements/${id}`);
export const getCoupsDeCoeur = () => api.get('/public/coups-de-coeur');
export const getPublicFormules = () => api.get('/public/formules');
export const getPublicFaq = () => api.get('/public/faq');
export const getPublicTemoignages = (type) => api.get('/public/temoignages', { params: { type_temoignage: type } });
export const getPublicTémoignages = getPublicTemoignages; // Alias with accent for French components
export const getPublicArticles = () => api.get('/public/articles');
export const getPublicArticle = (slug) => api.get(`/public/articles/${slug}`);
export const estimerRevenus = (data) => api.post('/public/estimateur', null, { params: data });
export const estimérRevenus = estimerRevenus; // Alias with accent for French components
export const simulerRentabilite = (data) => api.post('/public/simulateur', null, { params: data });

// Leads
export const createLead = (data) => api.post('/leads', data);

// Reservations (public can create)
export const createReservation = (data) => api.post('/reservations', data);

// Admin endpoints
export const getDashboardStats = () => api.get('/dashboard/stats');
export const seedData = () => api.post('/seed');

// Logements
export const getLogements = (params) => api.get('/logements', { params });
export const getLogement = (id) => api.get(`/logements/${id}`);
export const createLogement = (data) => api.post('/logements', data);
export const updateLogement = (id, data) => api.put(`/logements/${id}`, data);
export const deleteLogement = (id) => api.delete(`/logements/${id}`);
export const toggleCoupDeCoeur = (id) => api.patch(`/logements/${id}/coup-de-coeur`);

// iCal Sync
export const getICalConfig = (id) => api.get(`/logements/${id}/ical-config`);
export const updateICalConfig = (id, data) => api.put(`/logements/${id}/ical-config`, data);
export const triggerICalSync = (id) => api.post(`/logements/${id}/ical-sync`);
export const getICalDashboard = () => api.get('/logements/ical/dashboard');
export const triggerICalSyncAll = () => api.post('/logements/ical/sync-all');
export const getICalExportUrl = (id) => `${API}/public/logements/${id}/calendar.ics`;

// Proprietaires
export const getProprietaires = () => api.get('/proprietaires');
export const getProprietaire = (id) => api.get(`/proprietaires/${id}`);
export const createProprietaire = (data) => api.post('/proprietaires', data);
export const updateProprietaire = (id, data) => api.put(`/proprietaires/${id}`, data);
export const deleteProprietaire = (id) => api.delete(`/proprietaires/${id}`);

// Reservations
export const getReservations = (params) => api.get('/reservations', { params });
export const getReservation = (id) => api.get(`/reservations/${id}`);
export const updateReservationStatut = (id, statut) => api.patch(`/reservations/${id}/statut`, null, { params: { statut } });

// Disponibilites
export const getDisponibilites = (logementId) => api.get(`/disponibilites/${logementId}`);
export const createDisponibilite = (data) => api.post('/disponibilites', null, { params: data });
export const deleteDisponibilite = (id) => api.delete(`/disponibilites/${id}`);

// Prestations
export const getPrestations = (params) => api.get('/prestations', { params });
export const createPrestation = (data) => api.post('/prestations', data);
export const deletePrestation = (id) => api.delete(`/prestations/${id}`);

// Incidents
export const getIncidents = (params) => api.get('/incidents', { params });
export const createIncident = (data) => api.post('/incidents', data);
export const updateIncident = (id, data) => api.patch(`/incidents/${id}`, null, { params: data });

// Leads (admin)
export const getLeads = (params) => api.get('/leads', { params });
export const updateLeadStatut = (id, statut) => api.patch(`/leads/${id}/statut`, null, { params: { statut } });

// Formules
export const getFormules = () => api.get('/formules');
export const createFormule = (data) => api.post('/formules', data);
export const updateFormule = (id, data) => api.put(`/formules/${id}`, data);

// Articles
export const getArticles = (params) => api.get('/articles', { params });
export const createArticle = (data) => api.post('/articles', data);
export const updateArticle = (id, data) => api.put(`/articles/${id}`, data);
export const deleteArticle = (id) => api.delete(`/articles/${id}`);

// FAQ
export const getFaqs = () => api.get('/faq');
export const createFaq = (data) => api.post('/faq', data);
export const updateFaq = (id, data) => api.put(`/faq/${id}`, data);
export const deleteFaq = (id) => api.delete(`/faq/${id}`);

// Temoignages
export const submitAvis = (data) => api.post('/public/avis', data);
export const getTemoignages = (type) => api.get('/temoignages', { params: { type_temoignage: type } });
export const createTemoignage = (data) => api.post('/temoignages', data);
export const updateTemoignage = (id, data) => api.put(`/temoignages/${id}`, data);
export const deleteTemoignage = (id) => api.delete(`/temoignages/${id}`);

// Messages auto
export const getMessagesAuto = () => api.get('/messages-auto');
export const createMessageAuto = (data) => api.post('/messages-auto', data);
export const updateMessageAuto = (id, data) => api.put(`/messages-auto/${id}`, data);

// Rapports
export const getRapports = (proprietaireId) => api.get('/rapports', { params: { proprietaire_id: proprietaireId } });
export const getRapportMensuel = (mois, annee, proprietaireId, logementId) => 
  api.get('/rapports/mensuel', { params: { mois, annee, proprietaire_id: proprietaireId, logement_id: logementId } });
export const generateRapportMensuelPdf = (mois, annee, proprietaireId) =>
  api.post('/rapports/mensuel/pdf', null, { params: { mois, annee, proprietaire_id: proprietaireId } });
export const generateRapport = (proprietaireId, periodeDebut, periodeFin) => 
  api.post('/rapports/generate', null, { params: { proprietaire_id: proprietaireId, periode_debut: periodeDebut, periode_fin: periodeFin } });
export const downloadRapport = (filename) => `${API}/rapports/download/${filename}`;

// Newsletter
export const subscribeNewsletter = (email) => api.post('/newsletter/subscribe', { email });
export const getNewsletterSubscribers = () => api.get('/newsletter');
export const getNewsletterStats = () => api.get('/newsletter/stats');
export const unsubscribeNewsletter = (id) => api.patch(`/newsletter/${id}/unsubscribe`);
export const deleteNewsletterSubscriber = (id) => api.delete(`/newsletter/${id}`);
